import { useFlags } from 'launchdarkly-react-client-sdk';
import useUI from 'hooks/use-ui';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import useProductQueryParams from 'src/hooks/use-product-query-params';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import type { ProductsFilterInput } from 'src/dispensary/hooks/use-dispensary-products';
import { useIsConciergeEnabled } from 'src/concierge/use-is-concierge-enabled';
import { useSeededConciergeNavigate } from 'src/dispensary/core-menu/components/header/concierge-nav-button/use-concierge-navigate';
import ROUTES from 'src/routes';
import type { ProductListConciergeEntry } from './product-list-concierge-content';
import {
  buildCategoryConciergeContent,
  buildProductSearchContext,
  buildSearchConciergeContent,
} from './product-list-concierge-content';

type Dispensary = Parameters<typeof useIsConciergeEnabled>[0];
type ProductListEntryInput = {
  productsFilter?: ProductsFilterInput;
  productsCount: number;
  loading: boolean;
  dispensary: Dispensary;
};

type PreparedEntry = { show: boolean; entry: ProductListConciergeEntry | null };

function selectedValues(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string' && !!item.trim());
  return typeof value === 'string' && value.trim() ? [value] : [];
}

function isEligibleRoute(route: string): boolean {
  const menuRoots = [ROUTES.STORES_CNAME, ROUTES.EMBEDDED_MENU_CNAME];
  const productPages = [ROUTES.ALL_PRODUCTS_SEARCH, ROUTES.PRODUCTS_CATEGORY, ROUTES.SUBCATEGORY];
  return menuRoots.some((root) => productPages.some((page) => route === `${root}${page}`));
}

/** Product-list-only entry. Reuses the filter sent with the displayed product query; never fetches products. */
export function useProductListConciergeContent({
  productsFilter,
  productsCount,
  loading,
  dispensary,
}: ProductListEntryInput): PreparedEntry {
  const flags = useFlags();
  const UI = useUI();
  const openWithSeed = useSeededConciergeNavigate();
  const conciergeEnabled = useIsConciergeEnabled(dispensary, UI);
  const { queryParams } = useProductQueryParams();
  const routeCategory = useDispensaryCategory();
  const { effects: menuEffects, terpenes: menuTerpenes, apolloValues: menuOptionsQuery } = useMenuFilterOptions({
    skipCurrentFilters: true,
  });

  const eligible =
    isEligibleRoute(UI.route) &&
    conciergeEnabled &&
    flags['ecomm.concierge.product-list-entry.rollout'] === true &&
    !loading &&
    // Effect and terpene context is translated from menu options.
    !menuOptionsQuery.loading &&
    (queryParams.page || 1) === 1 &&
    productsCount > 6 &&
    Boolean(dispensary?.id) &&
    (UI.onSearchPage === true || Boolean(routeCategory?.value));
  if (!eligible) return { show: false, entry: null };

  const resolvedFilter = productsFilter ?? {};
  const context = buildProductSearchContext({
    resolvedFilter,
    selectedEffects: selectedValues(queryParams.effects),
    menuEffects: Array.isArray(menuEffects) ? menuEffects : [],
    menuTerpenes: Array.isArray(menuTerpenes) ? menuTerpenes : [],
  });
  const searchTerm = productsFilter?.query ?? '';
  // Filters-only copy describes exactly the context sent to the model.
  const hasFilters = Object.keys(context).length > 0;
  const categoryName = routeCategory?.value ?? '';
  // A category route with an active search query shows search results, not a pure category listing.
  // Prefer search copy whenever the resolved filter carries a query, regardless of route surface.
  const isSearchSurface = UI.onSearchPage === true || searchTerm.trim().length > 0;

  const content = isSearchSurface
    ? buildSearchConciergeContent({ searchTerm, hasFilters, context })
    : buildCategoryConciergeContent({ categoryName, context });
  if (!content.seed) return { show: false, entry: null };

  const { seed } = content;
  return { show: true, entry: { content, onClick: () => openWithSeed(seed, 'product_list') } };
}
